
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Box, Button, FormGroup, FormControlLabel, Checkbox, Typography, Divider } from '@mui/material';
import * as d3 from 'd3';
import { useAuth } from 'react-oidc-context';
import { apiGet, apiGetPdf, apiPost, handleApiError } from '../utils/ApiClient';
import { useData } from "../utils/DataContext";
import { useNavigate } from 'react-router-dom';
import { getJapanDateString } from '../utils/DateUtils';
import { fetchAndOpenPdfById } from '../utils/PdfUtils';
import ConfirmDialog from '../components/ConfirmDialog';
import LoadingOverlay from '../components/LoadingOverlay';

type QS7Data = {
  geneName: string;
  drn: number;
  cycleNumber: number;
};

type CurveReportFormProps = {
  iraiId: string;
  onClose: () => void;
};

const COLORS = d3.schemeCategory10.concat(d3.schemeSet2).concat(d3.schemeSet3);

const CurveReportForm: React.FC<CurveReportFormProps> = ({ iraiId, onClose }) => {
  const auth = useAuth();
  const { iraiData, refreshData } = useData();
  const [data, setData] = useState<QS7Data[]>([]);
  const [visibleGenes, setVisibleGenes] = useState<{ [key: string]: boolean }>({});
  const [approvalConfirmOpen, setApprovalConfirmOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const svgRef = useRef<SVGSVGElement>(null);
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      const { data: jsonData } = await apiGet<QS7Data[]>(`/getQs7Data/${iraiId}`, auth.user?.access_token);
      setData(jsonData);
      // Initialize visibility state for all genes
      const genes = Array.from(new Set(jsonData.map((d: QS7Data) => d.geneName))) as string[];
      const initialVisibility = Object.fromEntries(genes.map(gene => [gene, true]));
      setVisibleGenes(initialVisibility);
    } catch (error) {
      console.error('Error fetching data:', handleApiError(error, 'Fetching QS7 data'));
    }
  }, [iraiId, auth.user?.access_token]);

  const drawChart = useCallback(() => {
    const margin = { top: 20, right: 150, bottom: 50, left: 50 };
    const width = 800 - margin.left - margin.right;
    const height = 400 - margin.top - margin.bottom;

    // Clear existing SVG
    d3.select(svgRef.current).selectAll("*").remove();

    const svg = d3.select(svgRef.current)
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);    // Create scales
    const x = d3.scaleLinear()
      .domain([0, d3.max(data, d => d.cycleNumber) || 0])
      .range([0, width]);

    // For log scale, filter out zero/negative values and set appropriate domain
    const validDrnValues = data.filter(d => d.drn > 0).map(d => d.drn);
    const minDrn = d3.min(validDrnValues) || 0.001;
    const maxDrn = d3.max(validDrnValues) || 1;

    const y = d3.scaleLog()
      .domain([minDrn, maxDrn])
      .range([height, 0]);    // Add axes
    svg.append('g')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(x));

    svg.append('g')
      .call(d3.axisLeft(y)
        .tickValues([0.001, 0.01, 0.1])
        .tickFormat(d3.format(".3f")));// Add axis labels
    // X-axis label
    svg.append('text')
      .attr('transform', `translate(${width / 2}, ${height + margin.bottom - 3})`)
      .style('text-anchor', 'middle')
      .style('font-size', '12px')
      .text('Cycle');

    // Y-axis label
    svg.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', 0 - margin.left)
      .attr('x', 0 - (height / 2))
      .attr('dy', '1em')
      .style('text-anchor', 'middle')
      .style('font-size', '12px')
      .text('dRn');

    // Group data by gene
    const groupedData = d3.group(data, d => d.geneName);    // Create line generator
    const line = d3.line<QS7Data>()
      .x(d => x(d.cycleNumber))
      .y(d => d.drn > 0 ? y(d.drn) : y(minDrn)) // Handle zero/negative values
      .curve(d3.curveBasis);

    // Draw lines for each visible gene
    Array.from(groupedData.entries()).forEach(([gene, values], i) => {
      if (visibleGenes[gene]) {
        svg.append('path')
          .datum(values)
          .attr('fill', 'none')
          .attr('stroke', COLORS[i % COLORS.length])
          .attr('stroke-width', 1.5)
          .attr('d', line);
      }
    });
    // Add legend
    const legend = svg.append('g')
      .attr('font-family', 'sans-serif')
      .attr('font-size', 10)
      .attr('text-anchor', 'start')
      .selectAll('g')
      .data(Array.from(groupedData.keys()))
      .enter().append('g')
      .attr('transform', (d, i) => `translate(${width + 40},${i * 11})`);

    legend.append('rect')
      .attr('x', 0)
      .attr('width', 19)
      .attr('height', 10)
      .attr('fill', (d, i) => COLORS[i % COLORS.length]);

    legend.append('text')
      .attr('x', 24)
      .attr('y', 9.5)
      .attr('dy', '0.32em')
      .text(d => d);
  }, [data, visibleGenes]);
  const handleToggleGene = (gene: string) => {
    setVisibleGenes(prev => ({
      ...prev,
      [gene]: !prev[gene]
    }));
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (data.length > 0 && svgRef.current) {
      drawChart();
    }
  }, [data, visibleGenes, drawChart]);

  const onApproval = () => {
    // 確認ダイアログを表示
    setApprovalConfirmOpen(true);
  };

  const handleApprovalConfirm = async () => {
    setApprovalConfirmOpen(false);
    setIsProcessing(true); // 処理開始、画面を非活性に

    // 承認処理の実装
    const targetItem = iraiData.find(item => item.id === iraiId);
    if (targetItem) {
      // オブジェクトのコピーを作成して更新（直接変更を避ける）
      const updatedItem = {
        ...targetItem,
        statusId: 34,
        approvalDate: getJapanDateString(),
        dataApprovalDoctorId: auth.user?.profile?.email || '',
        dataApprovalDoctorName: auth.user?.profile?.name || auth.user?.profile?.email || '',
      };
      try {
        await apiPost("/approval", updatedItem, auth.user?.access_token);
      } catch (error) {
        console.error("Error submitting irai:", handleApiError(error, 'Submitting irai'));
        setIsProcessing(false); // エラー時は処理終了
        return; // 承認APIエラー時のみ遷移しない
      }
      // 報告書PDFを取得（失敗しても一覧遷移は行う）
      try {
        try {
          await fetchAndOpenPdfById(apiGetPdf, "/getReportForm", updatedItem.id, auth.user?.access_token);
        } catch {
          await fetchAndOpenPdfById(apiGetPdf, "/generateReportFormById", updatedItem.id, auth.user?.access_token);
        }
      } catch (error) {
        console.error("Error fetching report PDF:", handleApiError(error, 'Fetching report PDF'));
      }
      await refreshData(true); // 強制更新
    }

    setIsProcessing(false); // 処理完了
    navigate("/iraiList");
  };

  const handleApprovalCancel = () => {
    setApprovalConfirmOpen(false);
  };

  const handleSelectAll = () => {
    setVisibleGenes(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(gene => {
        updated[gene] = true;
      });
      return updated;
    });
  };

  const handleClearAll = () => {
    setVisibleGenes(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(gene => {
        updated[gene] = false;
      });
      return updated;
    });
  };
  return (
    <Box sx={{
      p: 3,
      minHeight: '100vh',
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      background: '#e0e0ff',
    }}>
      {/* パネル＋チャートを中央揃え */}
      <Box sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'stretch',
        justifyContent: 'center',
        gap: 6, // パネルとチャートの間の幅
        width: '100%',
        maxWidth: '1200px',
        mx: 'auto',
      }}>
        {/* コントロールパネル */}
        <Box sx={{
          minWidth: 300,
          maxWidth: 300,
          height: 500,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          background: '#faf8f5',
          borderRadius: 2,
          boxShadow: 1,
          p: 2,
        }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <Button variant="outlined" size="small" onClick={handleSelectAll}>
              Select All
            </Button>
            <Button variant="outlined" size="small" onClick={handleClearAll}>
              Clear All
            </Button>
          </Box>
          <FormGroup sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 0.5,
            width: '100%',
            flex: 1,
            overflow: 'auto'
          }}>
            {Object.keys(visibleGenes).map((gene) => (
              <FormControlLabel
                key={gene}
                control={
                  <Checkbox
                    checked={visibleGenes[gene]}
                    onChange={() => handleToggleGene(gene)}
                    size="small"
                  />
                }
                label={gene}
                sx={{
                  margin: 0,
                  fontSize: '0.8rem',
                  '& .MuiFormControlLabel-label': {
                    fontSize: '0.8rem'
                  }
                }}
              />
            ))}
          </FormGroup>

          {/* 依頼情報表示エリア */}
          <Divider sx={{ my: 1 }} />
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            p: 1,
            backgroundColor: '#f5f5f5',
            borderRadius: 1,
            flexShrink: 0
          }}>
            <Typography variant="body2" sx={{ fontSize: '1rem' }}>
              依頼ID: {iraiId}
            </Typography>
          </Box>
        </Box>
        {/* チャート部分 */}
        <Box sx={{
          minWidth: 820,
          maxWidth: 820,
          height: 500,
          background: '#fff',
          borderRadius: 2,
          boxShadow: 1,
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}>
          <svg ref={svgRef}></svg>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', maxWidth: '1200px', mt: 3 }}>
        <Button variant="contained" onClick={onClose}>
          戻る
        </Button>
        <Button variant="contained" color="secondary" onClick={onApproval} sx={{ ml: 2 }}>
          承認
        </Button>
      </Box>
      <ConfirmDialog
        open={approvalConfirmOpen}
        title="承認確認"
        message="増幅データを承認します。承認後、本依頼は完了となります。<br />よろしいですか？"
        confirmText="はい"
        cancelText="いいえ"
        confirmColor="error"
        onConfirm={handleApprovalConfirm}
        onCancel={handleApprovalCancel}
      />

      <LoadingOverlay
        open={isProcessing}
        message="承認処理中です。しばらくお待ちください。"
      />
    </Box>
  );
};

export default CurveReportForm;

